<script setup>
import { computed, onMounted } from 'vue';
import { useI18n } from 'vue-i18n';
import { useRoute, useRouter } from 'vue-router';
import { useStore, useStoreGetters } from 'dashboard/composables/store';
import { useAlert } from 'dashboard/composables';
import { useLocalizedTime } from 'shared/composables/useLocalizedTime';

import {
  BaseTable,
  BaseTableRow,
  BaseTableCell,
} from 'dashboard/components-next/table';
import PaginationFooter from 'dashboard/components-next/pagination/PaginationFooter.vue';
import Spinner from 'dashboard/components-next/spinner/Spinner.vue';
import Button from 'dashboard/components-next/button/Button.vue';

const { t } = useI18n();
const { dateTime } = useLocalizedTime();
const route = useRoute();
const router = useRouter();
const store = useStore();
const getters = useStoreGetters();

const records = computed(
  () => getters['scheduledMessages/getAllScheduledMessages'].value
);
const meta = computed(() => getters['scheduledMessages/getMeta'].value);
const uiFlags = computed(() => getters['scheduledMessages/getUIFlags'].value);

const routerPage = computed(() => Number(route.query.page ?? 1));

const tableHeaders = computed(() => [
  t('SCHEDULED_MESSAGES.TABLE_HEADER.CONTACT'),
  t('SCHEDULED_MESSAGES.TABLE_HEADER.CHANNEL'),
  t('SCHEDULED_MESSAGES.TABLE_HEADER.PREVIEW'),
  t('SCHEDULED_MESSAGES.TABLE_HEADER.SCHEDULED_BY'),
  t('SCHEDULED_MESSAGES.TABLE_HEADER.SCHEDULED_AT'),
  t('SCHEDULED_MESSAGES.TABLE_HEADER.STATUS'),
  '',
]);

const fetchScheduledMessages = page => {
  store.dispatch('scheduledMessages/get', page);
};

onMounted(() => fetchScheduledMessages(routerPage.value));

const onPageChange = page => {
  router.push({ name: 'scheduled_messages_index', query: { page } });
};

const statusColor = status => {
  const colorMap = {
    pending: 'text-n-blue-11 bg-n-blue-3',
    sent: 'text-n-teal-11 bg-n-teal-3',
    failed: 'text-n-ruby-11 bg-n-ruby-3',
    cancelled: 'text-n-slate-11 bg-n-slate-3',
  };
  return colorMap[status] || colorMap.pending;
};

const statusLabel = status =>
  t(`SCHEDULED_MESSAGES.STATUS.${status.toUpperCase()}`);

const preview = record => record.content || record.template_id || '';

const goToConversation = record => {
  router.push({
    name: 'inbox_conversation',
    params: { conversation_id: record.conversation_id },
  });
};

const cancelScheduledMessage = async record => {
  try {
    await store.dispatch('scheduledMessages/cancel', {
      conversationId: record.conversation_id,
      id: record.id,
    });
    useAlert(t('SCHEDULED_MESSAGES.CANCEL_SUCCESS'));
  } catch (error) {
    useAlert(t('SCHEDULED_MESSAGES.CANCEL_ERROR'));
  }
};

const retryScheduledMessage = async record => {
  try {
    await store.dispatch('scheduledMessages/retry', {
      conversationId: record.conversation_id,
      id: record.id,
    });
    useAlert(t('SCHEDULED_MESSAGES.RETRY_SUCCESS'));
  } catch (error) {
    useAlert(t('SCHEDULED_MESSAGES.RETRY_ERROR'));
  }
};
</script>

<template>
  <section class="flex flex-col w-full h-full overflow-hidden bg-n-surface-1">
    <header class="sticky top-0 z-10 px-6">
      <div class="w-full max-w-5xl mx-auto">
        <div class="flex items-center h-20">
          <span class="text-heading-1 text-n-slate-12">
            {{ t('SCHEDULED_MESSAGES.HEADER_TITLE') }}
          </span>
        </div>
      </div>
    </header>
    <main class="flex-1 px-6 overflow-y-auto">
      <div class="w-full max-w-5xl mx-auto py-4">
        <div
          v-if="uiFlags.isFetching"
          class="flex items-center justify-center py-10 text-n-slate-11"
        >
          <Spinner />
        </div>
        <div v-else class="flex flex-col">
          <BaseTable
            :headers="tableHeaders"
            :items="records"
            :no-data-message="t('SCHEDULED_MESSAGES.EMPTY_STATE')"
          >
            <template #row="{ items }">
              <BaseTableRow
                v-for="record in items"
                :key="record.id"
                :item="record"
                class="cursor-pointer"
                @click="goToConversation(record)"
              >
                <template #default>
                  <BaseTableCell>
                    <span class="text-body-main text-n-slate-12">
                      {{ record.contact?.name }}
                    </span>
                  </BaseTableCell>
                  <BaseTableCell>
                    <span class="text-body-main text-n-slate-11">
                      {{ record.inbox?.name }}
                    </span>
                  </BaseTableCell>
                  <BaseTableCell>
                    <span
                      class="text-body-main text-n-slate-11 line-clamp-1 max-w-xs"
                    >
                      {{ preview(record) }}
                    </span>
                  </BaseTableCell>
                  <BaseTableCell>
                    <span class="text-body-main text-n-slate-11">
                      {{ record.created_by?.name }}
                    </span>
                  </BaseTableCell>
                  <BaseTableCell>
                    <span
                      class="text-body-main text-n-slate-11 whitespace-nowrap"
                    >
                      {{ dateTime(record.scheduled_at) }}
                    </span>
                  </BaseTableCell>
                  <BaseTableCell>
                    <span
                      class="rounded-full px-2 py-0.5 text-xs font-medium"
                      :class="statusColor(record.status)"
                      :title="
                        record.status === 'failed' ? record.error_message : ''
                      "
                    >
                      {{ statusLabel(record.status) }}
                    </span>
                  </BaseTableCell>
                  <BaseTableCell>
                    <Button
                      v-if="record.status === 'pending'"
                      :label="t('SCHEDULED_MESSAGES.CANCEL')"
                      slate
                      faded
                      sm
                      @click.stop="cancelScheduledMessage(record)"
                    />
                    <Button
                      v-else-if="record.status === 'failed'"
                      :label="t('SCHEDULED_MESSAGES.RETRY')"
                      slate
                      faded
                      sm
                      @click.stop="retryScheduledMessage(record)"
                    />
                  </BaseTableCell>
                </template>
              </BaseTableRow>
            </template>
          </BaseTable>
          <PaginationFooter
            :current-page="Number(meta.currentPage)"
            :total-items="meta.totalCount"
            :items-per-page="25"
            class="!px-0"
            @update:current-page="onPageChange"
          />
        </div>
      </div>
    </main>
  </section>
</template>
